import * as React from 'react';
import { ButtonCoreSize } from 'omakase/Button/ButtonCore';
import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
  OmakasePolymorphicRootComponent,
} from '../utils/OmakasePolymorphicComponent';
import { Theme, css } from '@emotion/react';
import { buttonIcon } from 'omakase/Button/buttonCssDefs';
import Anchor from 'components/Anchor';

import { ButtonIconProps } from 'omakase/Button/buttonIcons';
import { plainButtonBase, plainButtonSize, plainButtonVariant } from './plainButtonCssDefs';

export type PlainButtonComponent = 'button' | typeof Anchor;
export type PlainButtonVariant = 'basic' | 'primary';

export interface PlainButtonBaseProps {
  /**
   * The variant / color style for the button
   */
  variant: PlainButtonVariant;
  /**
   * The aria-label for the button. Be sure to provide this if the button's text content
   * does not make it's purpose apparent
   */
  'aria-label'?: string;
  /**
   * Whether or not the component is disabled
   */
  disabled?: boolean;
  /**
   * The href for an anchor component
   */
  href?: string;
  /**
   * The click event
   */
  onClick?(e?: React.MouseEvent): void;
  /**
   * The size of the button and icon
   */
  size: ButtonCoreSize;
  className?: string;
}

export type PlainButtonProps = PlainButtonBaseProps & ButtonIconProps;

const PlainButtonDefaultElement = 'button';

const PlainButton: OmakasePolymorphicComponent<PlainButtonProps, typeof PlainButtonDefaultElement> =
  React.forwardRef(
    <E extends React.ElementType = typeof PlainButtonDefaultElement>(
      {
        'aria-label': al,
        children,
        component,
        disabled = false,
        href,
        onClick,
        size,
        iconStart,
        iconEnd,
        variant,
        ...rest
      }: OmakasePolymorphicComponentProps<PlainButtonProps, E>,
      ref: React.Ref<HTMLButtonElement> | undefined
    ) => {
      const sharedProps = {
        'aria-label': al,
        css: (theme: Theme) => [
          plainButtonBase(theme),
          plainButtonSize(theme, size),
          plainButtonVariant(theme, variant),
        ],
        onClick: disabled ? undefined : onClick,
        ...rest,
      };

      const Component: React.ElementType = component || PlainButtonDefaultElement;

      const buttonProps: { [key: string]: boolean | string | undefined } = {};
      if (Component === 'button') {
        buttonProps.disabled = disabled || undefined;
      } else {
        buttonProps['aria-disabled'] = disabled || undefined;
        buttonProps.href = disabled ? undefined : href;
      }

      const inner = (
        <>
          {iconStart && (
            <span
              css={(theme: Theme) => [
                buttonIcon(theme, { position: 'start', size: 'small' }),
                css`
                  margin-right: ${theme.spacing(1)}px;
                `,
              ]}
            >
              {iconStart}
            </span>
          )}
          <span>{children}</span>
          {iconEnd && (
            <span
              css={(theme: Theme) => [
                buttonIcon(theme, { position: 'end', size }),
                css`
                  margin-left: ${theme.spacing(1)}px;
                `,
              ]}
            >
              {iconEnd}
            </span>
          )}
        </>
      );

      return (
        <OmakasePolymorphicRootComponent
          ref={ref}
          component={Component}
          {...buttonProps}
          {...sharedProps}
        >
          {inner}
        </OmakasePolymorphicRootComponent>
      );
    }
  );

export default PlainButton;
